import { motion, AnimatePresence } from "motion/react";
import {
    TrendingUp,
    TrendingDown,
    PieChart,
    BarChart3,
    Download,
    Filter,
    Search,
    ArrowUpRight,
    ChevronDown,
    X,
    Calendar,
    DollarSign,
    Activity
} from "lucide-react";
import { useState, useMemo } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart as RechartsPie, Pie, Cell } from 'recharts';

import { useRWAStore, Asset } from "../store/rwa-store";

type TimeRange = '1D' | '1W' | '1M' | '3M' | '1Y' | 'ALL';

interface PortfolioPosition {
    assetId: string;
    amount: number;
    avgCost: number;
    purchaseDate: string;
    asset: Asset;
    currentValue: number;
    costBasis: number;
    pnl: number;
    pnlPercent: number;
    currentPrice: number;
    change24h: number;
}

export default function PortfolioView() {
    const { currentUser, assets, transactions } = useRWAStore();
    const [timeRange, setTimeRange] = useState<TimeRange>('1M');
    const [searchTerm, setSearchTerm] = useState("");
    const [sortBy, setSortBy] = useState<'value' | 'gain' | 'name'>('value');
    const [selectedPosition, setSelectedPosition] = useState<string | null>(null);

    // Calculate portfolio data
    const portfolioData = useMemo(() => {
        if (!currentUser?.portfolio) return [];

        return currentUser.portfolio.map(item => {
            const asset = assets.find(a => a.id === item.assetId);
            if (!asset) return null;

            const currentValue = item.amount * asset.price;
            const costBasis = item.amount * item.avgCost;
            const pnl = currentValue - costBasis;
            const pnlPercent = costBasis > 0 ? (pnl / costBasis) * 100 : 0;

            return {
                ...item,
                asset,
                currentValue,
                costBasis,
                pnl,
                pnlPercent,
                currentPrice: asset.price,
                change24h: asset.change24h
            };
        }).filter(Boolean) as PortfolioPosition[];
    }, [currentUser, assets]);

    // Totals
    const totalValue = portfolioData.reduce((acc, curr) => acc + curr.currentValue, 0);
    const totalCost = portfolioData.reduce((acc, curr) => acc + curr.costBasis, 0);
    const totalPnL = totalValue - totalCost;
    const totalPnLPercent = totalCost > 0 ? (totalPnL / totalCost) * 100 : 0;

    // Get history data based on time range
    const historyData = useMemo(() => {
        if (!currentUser?.portfolioHistory) return [];
        const history = currentUser.portfolioHistory;

        switch (timeRange) {
            case '1D': return history.slice(-1);
            case '1W': return history.slice(-7);
            case '1M': return history;
            case '3M': return history; // Would need more data
            case '1Y': return history;
            case 'ALL': return history;
            default: return history;
        }
    }, [currentUser?.portfolioHistory, timeRange]);

    // Allocation data for pie chart
    const allocationData = useMemo(() => {
        const byType: Record<string, number> = {};
        portfolioData.forEach(item => {
            const type = item.asset.type;
            byType[type] = (byType[type] || 0) + item.currentValue;
        });

        const COLORS: Record<string, string> = {
            'crypto': '#10b981',
            'real-estate': '#3b82f6',
            'bond': '#8b5cf6',
            'commodity': '#f59e0b',
            'art': '#ec4899'
        };

        return Object.entries(byType).map(([type, value]) => ({
            name: type.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()),
            value,
            color: COLORS[type] || '#6b7280',
            percent: (value / totalValue) * 100
        }));
    }, [portfolioData, totalValue]);

    // Sort and filter
    const sortedPortfolio = useMemo(() => {
        const filtered = portfolioData.filter(item =>
            item.asset.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.asset.symbol.toLowerCase().includes(searchTerm.toLowerCase())
        );

        switch (sortBy) {
            case 'value':
                return filtered.sort((a, b) => b.currentValue - a.currentValue);
            case 'gain':
                return filtered.sort((a, b) => b.pnlPercent - a.pnlPercent);
            case 'name':
                return filtered.sort((a, b) => a.asset.name.localeCompare(b.asset.name));
            default:
                return filtered;
        }
    }, [portfolioData, searchTerm, sortBy]);

    // Position transactions
    const positionTransactions = useMemo(() => {
        if (!selectedPosition) return [];
        return transactions.filter(tx => tx.assetId === selectedPosition);
    }, [selectedPosition, transactions]);

    if (!currentUser) return null;

    return (
        <div className="max-w-7xl mx-auto space-y-8">
            {/* HEADER */}
            <header className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight mb-2">Portfolio</h1>
                    <p className="text-neutral-400">Track your investments and performance</p>
                </div>
                <div className="flex gap-3">
                    <button className="flex items-center gap-2 px-4 py-2 bg-neutral-900 border border-neutral-800 rounded-xl text-sm font-medium hover:bg-neutral-800 transition-colors">
                        <Filter className="w-4 h-4" /> Filters
                    </button>
                    <button className="flex items-center gap-2 px-4 py-2 bg-white text-black rounded-xl text-sm font-bold hover:bg-neutral-200 transition-colors">
                        <Download className="w-4 h-4" /> Export
                    </button>
                </div>
            </header>

            {/* SUMMARY CARDS */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <SummaryCard
                    label="Total Value"
                    value={`$${totalValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
                    icon={DollarSign}
                    trend={currentUser.portfolioHistory && currentUser.portfolioHistory.length > 1
                        ? ((totalValue - currentUser.portfolioHistory[currentUser.portfolioHistory.length - 2].value) / currentUser.portfolioHistory[currentUser.portfolioHistory.length - 2].value) * 100
                        : 0}
                />
                <SummaryCard
                    label="Total P&L"
                    value={`${totalPnL >= 0 ? '+' : ''}$${Math.abs(totalPnL).toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
                    icon={totalPnL >= 0 ? TrendingUp : TrendingDown}
                    trend={totalPnLPercent}
                    isPositive={totalPnL >= 0}
                />
                <SummaryCard
                    label="Cost Basis"
                    value={`$${totalCost.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
                    icon={Activity}
                />
                <SummaryCard
                    label="Positions"
                    value={portfolioData.length.toString()}
                    icon={PieChart}
                />
            </div>

            {/* CHARTS ROW */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Performance Chart */}
                <div className="lg:col-span-2 bg-neutral-900/40 border border-neutral-800 rounded-2xl p-6">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                        <h3 className="text-lg font-bold flex items-center gap-2">
                            <BarChart3 className="w-5 h-5 text-emerald-500" />
                            Performance
                        </h3>
                        <div className="flex bg-neutral-950 rounded-lg p-1 border border-neutral-800">
                            {(['1D', '1W', '1M', '3M', '1Y', 'ALL'] as TimeRange[]).map(range => (
                                <button
                                    key={range}
                                    onClick={() => setTimeRange(range)}
                                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${timeRange === range
                                        ? 'bg-neutral-800 text-white'
                                        : 'text-neutral-500 hover:text-neutral-300'
                                        }`}
                                >
                                    {range}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={historyData}>
                                <defs>
                                    <linearGradient id="portfolioGradient" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <XAxis
                                    dataKey="date"
                                    stroke="#525252"
                                    fontSize={12}
                                    tickLine={false}
                                    axisLine={false}
                                />
                                <YAxis
                                    stroke="#525252"
                                    fontSize={12}
                                    tickLine={false}
                                    axisLine={false}
                                    tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                                />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: '#171717',
                                        border: '1px solid #262626',
                                        borderRadius: '12px',
                                        padding: '12px'
                                    }}
                                    formatter={(value: number) => [`$${value.toLocaleString()}`, 'Value']}
                                />
                                <Area
                                    type="monotone"
                                    dataKey="value"
                                    stroke="#10b981"
                                    strokeWidth={2}
                                    fillOpacity={1}
                                    fill="url(#portfolioGradient)"
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Allocation Pie */}
                <div className="bg-neutral-900/40 border border-neutral-800 rounded-2xl p-6">
                    <h3 className="text-lg font-bold mb-6 flex items-center gap-2">
                        <PieChart className="w-5 h-5 text-blue-500" />
                        Allocation
                    </h3>
                    <div className="h-48 mb-4">
                        <ResponsiveContainer width="100%" height="100%">
                            <RechartsPie>
                                <Pie
                                    data={allocationData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={50}
                                    outerRadius={70}
                                    paddingAngle={4}
                                    dataKey="value"
                                >
                                    {allocationData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: '#171717',
                                        border: '1px solid #262626',
                                        borderRadius: '8px'
                                    }}
                                    formatter={(value: number) => [`$${value.toLocaleString()}`, '']}
                                />
                            </RechartsPie>
                        </ResponsiveContainer>
                    </div>
                    <div className="space-y-2">
                        {allocationData.map(item => (
                            <div key={item.name} className="flex items-center justify-between text-sm">
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                                    <span className="text-neutral-400">{item.name}</span>
                                </div>
                                <span className="font-mono text-white">{item.percent.toFixed(1)}%</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* HOLDINGS TABLE */}
            <div className="bg-neutral-900/40 border border-neutral-800 rounded-2xl overflow-hidden">
                <div className="p-6 border-b border-neutral-800 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <h3 className="text-lg font-bold">Holdings</h3>
                    <div className="flex gap-3 w-full md:w-auto">
                        <div className="relative flex-1 md:flex-initial">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search..."
                                className="w-full md:w-48 pl-9 pr-4 py-2 bg-neutral-950 border border-neutral-800 rounded-lg text-sm focus:outline-hidden focus:border-neutral-700"
                            />
                        </div>
                        <div className="relative">
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value as 'value' | 'gain' | 'name')}
                                className="appearance-none px-4 py-2 pr-8 bg-neutral-950 border border-neutral-800 rounded-lg text-sm focus:outline-hidden focus:border-neutral-700"
                            >
                                <option value="value">Sort by Value</option>
                                <option value="gain">Sort by Gain</option>
                                <option value="name">Sort by Name</option>
                            </select>
                            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500 pointer-events-none" />
                        </div>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full min-w-[800px]">
                        <thead className="bg-neutral-950/50 text-xs uppercase text-neutral-500 font-medium">
                            <tr>
                                <th className="px-6 py-4 text-left">Asset</th>
                                <th className="px-6 py-4 text-right">Amount</th>
                                <th className="px-6 py-4 text-right">Avg Cost</th>
                                <th className="px-6 py-4 text-right">Current Price</th>
                                <th className="px-6 py-4 text-right">Value</th>
                                <th className="px-6 py-4 text-right">P&L</th>
                                <th className="px-6 py-4 text-right">24h</th>
                                <th className="px-6 py-4"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-neutral-800/50">
                            {sortedPortfolio.map((item) => (
                                <motion.tr
                                    key={item.assetId}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="hover:bg-neutral-900/50 transition-colors cursor-pointer group"
                                    onClick={() => setSelectedPosition(item.assetId)}
                                >
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-xl overflow-hidden bg-neutral-800">
                                                {item.asset.images?.[0] && (
                                                    // eslint-disable-next-line @next/next/no-img-element
                                                    <img
                                                        src={item.asset.images[0]}
                                                        alt={item.asset.name}
                                                        className="w-full h-full object-cover"
                                                    />
                                                )}
                                            </div>
                                            <div>
                                                <div className="font-medium text-white">{item.asset.name}</div>
                                                <div className="text-xs text-neutral-500 font-mono">{item.asset.symbol}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-right font-mono">{item.amount.toLocaleString(undefined, { maximumFractionDigits: 4 })}</td>
                                    <td className="px-6 py-4 text-right font-mono text-neutral-400">${item.avgCost.toLocaleString()}</td>
                                    <td className="px-6 py-4 text-right font-mono">${item.currentPrice.toLocaleString()}</td>
                                    <td className="px-6 py-4 text-right font-mono font-medium">${item.currentValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                                    <td className="px-6 py-4 text-right">
                                        <div className={`font-mono ${item.pnl >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                                            {item.pnl >= 0 ? '+' : '-'}${Math.abs(item.pnl).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                        </div>
                                        <div className={`text-xs ${item.pnlPercent >= 0 ? 'text-emerald-500/70' : 'text-red-500/70'}`}>
                                            {item.pnlPercent >= 0 ? '+' : ''}{item.pnlPercent.toFixed(2)}%
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <span className={`px-2 py-1 rounded-md text-xs font-medium ${item.change24h >= 0
                                            ? 'bg-emerald-500/10 text-emerald-500'
                                            : 'bg-red-500/10 text-red-500'
                                            }`}>
                                            {item.change24h >= 0 ? '+' : ''}{item.change24h}%
                                        </span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <button className="opacity-0 group-hover:opacity-100 p-2 hover:bg-neutral-800 rounded-lg transition-all">
                                            <ArrowUpRight className="w-4 h-4" />
                                        </button>
                                    </td>
                                </motion.tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {sortedPortfolio.length === 0 && (
                    <div className="p-12 text-center text-neutral-500">
                        No holdings found. Start investing to build your portfolio.
                    </div>
                )}
            </div>

            {/* POSITION DETAIL MODAL */}
            <AnimatePresence>
                {selectedPosition && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-xs p-4">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="bg-neutral-900 border border-neutral-800 rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden"
                        >
                            {(() => {
                                const position = portfolioData.find(p => p.assetId === selectedPosition);
                                if (!position) return null;

                                return (
                                    <>
                                        <div className="p-6 border-b border-neutral-800 flex justify-between items-start">
                                            <div className="flex items-center gap-4">
                                                <div className="w-14 h-14 rounded-xl overflow-hidden bg-neutral-800">
                                                    {position.asset.images?.[0] && (
                                                        // eslint-disable-next-line @next/next/no-img-element
                                                        <img
                                                            src={position.asset.images[0]}
                                                            alt={position.asset.name}
                                                            className="w-full h-full object-cover"
                                                        />
                                                    )}
                                                </div>
                                                <div>
                                                    <h2 className="text-xl font-bold">{position.asset.name}</h2>
                                                    <div className="text-neutral-400 text-sm">{position.asset.symbol} • {position.asset.type}</div>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => setSelectedPosition(null)}
                                                className="p-2 hover:bg-neutral-800 rounded-lg"
                                            >
                                                <X className="w-5 h-5" />
                                            </button>
                                        </div>

                                        <div className="p-6 space-y-6 overflow-y-auto max-h-[60vh]">
                                            {/* Position Stats */}
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                                <div className="bg-neutral-950 p-4 rounded-xl">
                                                    <div className="text-xs text-neutral-500 mb-1">Amount</div>
                                                    <div className="font-mono font-bold">{position.amount.toLocaleString(undefined, { maximumFractionDigits: 4 })}</div>
                                                </div>
                                                <div className="bg-neutral-950 p-4 rounded-xl">
                                                    <div className="text-xs text-neutral-500 mb-1">Current Value</div>
                                                    <div className="font-mono font-bold">${position.currentValue.toLocaleString()}</div>
                                                </div>
                                                <div className="bg-neutral-950 p-4 rounded-xl">
                                                    <div className="text-xs text-neutral-500 mb-1">Cost Basis</div>
                                                    <div className="font-mono">${position.costBasis.toLocaleString()}</div>
                                                </div>
                                                <div className="bg-neutral-950 p-4 rounded-xl">
                                                    <div className="text-xs text-neutral-500 mb-1">Total P&L</div>
                                                    <div className={`font-mono font-bold ${position.pnl >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                                                        {position.pnl >= 0 ? '+' : ''}${position.pnl.toLocaleString()}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Transaction History */}
                                            <div>
                                                <h3 className="text-sm font-bold uppercase text-neutral-500 mb-3">Transaction History</h3>
                                                <div className="space-y-2">
                                                    {positionTransactions.length > 0 ? positionTransactions.map(tx => (
                                                        <div key={tx.id} className="flex justify-between items-center p-3 bg-neutral-950 rounded-lg">
                                                            <div className="flex items-center gap-3">
                                                                <div className={`w-2 h-2 rounded-full ${tx.status === 'completed' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                                                                <div>
                                                                    <div className="text-sm font-medium capitalize">{tx.type}</div>
                                                                    <div className="text-xs text-neutral-500">{new Date(tx.timestamp).toLocaleDateString()}</div>
                                                                </div>
                                                            </div>
                                                            <div className="text-right">
                                                                <div className="font-mono text-sm">${tx.amount.toLocaleString()}</div>
                                                                {tx.txHash && (
                                                                    <div className="text-xs text-neutral-500 font-mono">{tx.txHash}</div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )) : (
                                                        <div className="text-sm text-neutral-500 text-center py-4">No transactions recorded</div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Purchase Info */}
                                            <div className="flex items-center gap-2 text-sm text-neutral-500">
                                                <Calendar className="w-4 h-4" />
                                                <span>First purchased on {position.purchaseDate}</span>
                                            </div>
                                        </div>
                                    </>
                                );
                            })()}
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
}

// Summary Card Component
function SummaryCard({
    label,
    value,
    icon: Icon,
    trend,
    isPositive
}: {
    label: string;
    value: string;
    icon: React.ComponentType<{ className?: string }>;
    trend?: number;
    isPositive?: boolean;
}) {
    const showTrend = trend !== undefined && trend !== 0;
    const trendIsPositive = isPositive !== undefined ? isPositive : trend && trend >= 0;

    return (
        <div className="bg-neutral-900/40 border border-neutral-800 rounded-2xl p-5 hover:border-neutral-700 transition-colors">
            <div className="flex justify-between items-start mb-4">
                <div className="p-2.5 bg-neutral-950 rounded-xl border border-neutral-800">
                    <Icon className="w-5 h-5 text-neutral-400" />
                </div>
                {showTrend && (
                    <span className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${trendIsPositive
                        ? 'bg-emerald-500/10 text-emerald-500'
                        : 'bg-red-500/10 text-red-500'
                        }`}>
                        {trendIsPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                        {Math.abs(trend).toFixed(1)}%
                    </span>
                )}
            </div>
            <div className="text-neutral-500 text-sm mb-1">{label}</div>
            <div className="text-2xl font-bold font-mono tracking-tight">{value}</div>
        </div>
    );
}
